import React, { useState, useEffect } from 'react';
import { useGetSettingsQuery, useUpdateSettingsMutation } from '../redux/api/roomBookingApi';

const ApplicationSettings = () => {
    // Redux State
    const { data: settingsData, isLoading, refetch } = useGetSettingsQuery();
    const [updateSettings, { isLoading: isUpdating }] = useUpdateSettingsMutation();

    const [formData, setFormData] = useState({
        maintenance_buffer_hours: 2,
        maintenance_buffer_minutes: 0,
        enable_guru_seva: false,
        enable_donations: false,
        enable_online_sevas: false,
        enable_events: true,
        enable_activities: true,
        enable_publications: true,
        enable_gallery: true,
        room_tax_enabled: true,
        room_tax_rate: 5,
        room_conv_enabled: true,
        room_conv_rate: 5,
        invoice_prefix: 'INV/',
        invoice_reset_period: 'none',
        invoice_padding: 4
    });
    const [message, setMessage] = useState({ type: '', text: '' });
    const [visitorReset, setVisitorReset] = useState({ loading: false, msg: '' });
    const [invoiceReset, setInvoiceReset] = useState({ loading: false, msg: '' });

    useEffect(() => {
        if (settingsData?.data) {
            setFormData({
                maintenance_buffer_hours: settingsData.data.maintenance_buffer_hours ?? 2,
                maintenance_buffer_minutes: settingsData.data.maintenance_buffer_minutes ?? 0,
                enable_guru_seva: settingsData.data.enable_guru_seva ?? false,
                enable_donations: settingsData.data.enable_donations ?? false,
                enable_online_sevas: settingsData.data.enable_online_sevas ?? false,
                enable_events: settingsData.data.enable_events ?? true,
                enable_activities: settingsData.data.enable_activities ?? true,
                enable_publications: settingsData.data.enable_publications ?? true,
                enable_gallery: settingsData.data.enable_gallery ?? true,
                room_tax_enabled: settingsData.data.room_tax_enabled !== undefined ? settingsData.data.room_tax_enabled : true,
                room_tax_rate: settingsData.data.room_tax_rate ?? 5,
                room_conv_enabled: settingsData.data.room_conv_enabled !== undefined ? settingsData.data.room_conv_enabled : true,
                room_conv_rate: settingsData.data.room_conv_rate ?? 5,
                invoice_prefix: settingsData.data.invoice_prefix ?? 'INV/',
                invoice_reset_period: settingsData.data.invoice_reset_period ?? 'none',
                invoice_padding: settingsData.data.invoice_padding ?? 4
            });
        }
    }, [settingsData]);



    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : (type === 'number' ? (parseFloat(value) || 0) : value)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });

        try {
            const res = await updateSettings(formData).unwrap();
            if (res.status === 'success') {
                setMessage({ type: 'success', text: 'Settings updated successfully!' });
                refetch();
            } else {
                setMessage({ type: 'error', text: 'Failed to update settings.' });
            }
        } catch (error) {
            console.error(error);
            setMessage({ type: 'error', text: 'Error saving settings.' });
        }
    };

    const handleResetVisitorCount = async () => {
        const confirmed = window.confirm(
            'Are you sure you want to reset the Visitor Counter to 0?\nThis action cannot be undone.'
        );
        if (!confirmed) return;

        setVisitorReset({ loading: true, msg: '' });
        try {
            const res = await fetch('/api/settings/visitor/reset', { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                setVisitorReset({ loading: false, msg: `✅ Visitor counter reset to 0 successfully.` });
            } else {
                setVisitorReset({ loading: false, msg: '❌ Reset failed. Please try again.' });
            }
        } catch (err) {
            setVisitorReset({ loading: false, msg: '❌ Network error. Reset failed.' });
        }
    };

    const handleResetInvoiceCounter = async () => {
        const confirmed = window.confirm(
            'Are you sure you want to reset the Invoice Counter?\n\nThis will delete ALL period counters and restart numbering from 1.\n\nThis action cannot be undone.'
        );
        if (!confirmed) return;

        setInvoiceReset({ loading: true, msg: '' });
        try {
            const res = await fetch('/api/settings/invoice/reset', { method: 'POST' });
            const data = await res.json();
            if (data.status === 'success') {
                setInvoiceReset({ loading: false, msg: `✅ Invoice counter reset successfully. Next invoice will start from 0001.` });
            } else {
                setInvoiceReset({ loading: false, msg: '❌ Reset failed. ' + (data.message || 'Please try again.') });
            }
        } catch (err) {
            setInvoiceReset({ loading: false, msg: '❌ Network error. Reset failed.' });
        }
    };

    // Build preview invoice number
    const buildPreviewInvoice = () => {
        const prefix = formData.invoice_prefix || 'INV/';
        const padding = parseInt(formData.invoice_padding) || 4;
        const seq = String(1).padStart(padding, '0');
        return `${prefix}${seq}`;
    };

    if (isLoading) return <div className="container p-5 text-center">Loading Settings...</div>;

    return (
        <div className="container mt-5 mb-5">
            <div className="card shadow-sm">
                <div className="card-header bg-white">
                    <h3 className="mb-0">Application Settings</h3>
                </div>
                <div className="card-body p-4">
                    {message.text && (
                        <div className={`alert alert-${message.type === 'error' ? 'danger' : 'success'}`}>
                            {message.text}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <h5 className="mb-3 text-secondary">Boooking &amp; Maintenance</h5>
                        <div className="alert alert-info small">
                            <strong>Maintenance Buffer:</strong> The time period after checkout where the room remains locked for cleaning. Set to 00:00 for no buffer.
                        </div>

                        <div className="row mb-4">
                            <div className="col-md-6">
                                <label className="form-label fw-bold">Maintenance Buffer (Hours)</label>
                                <input
                                    type="number"
                                    min="0"
                                    name="maintenance_buffer_hours"
                                    className="form-control"
                                    value={formData.maintenance_buffer_hours}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-bold">Maintenance Buffer (Minutes)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="59"
                                    name="maintenance_buffer_minutes"
                                    className="form-control"
                                    value={formData.maintenance_buffer_minutes}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <h5 className="mb-3 mt-4 text-secondary">Module Visibility - Backend Processing &amp; Reports</h5>
                        <div className="alert alert-warning small">
                            <strong>Note:</strong> Disabling these will hide them from Admin Reports and prevent backend processing. Logic for Frontend Menu Links is managed separately.
                        </div>

                        <div className="row g-3 mb-4">
                            <div className="col-md-4">
                                <div className="form-check form-switch p-3 border rounded bg-light">
                                    <input className="form-check-input" type="checkbox" role="switch" id="guru_seva" name="enable_guru_seva" checked={formData.enable_guru_seva} onChange={handleChange} />
                                    <label className="form-check-label fw-bold" htmlFor="guru_seva">Enable Guru Seva</label>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-check form-switch p-3 border rounded bg-light">
                                    <input className="form-check-input" type="checkbox" role="switch" id="donations" name="enable_donations" checked={formData.enable_donations} onChange={handleChange} />
                                    <label className="form-check-label fw-bold" htmlFor="donations">Enable Donations</label>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-check form-switch p-3 border rounded bg-light">
                                    <input className="form-check-input" type="checkbox" role="switch" id="online_sevas" name="enable_online_sevas" checked={formData.enable_online_sevas} onChange={handleChange} />
                                    <label className="form-check-label fw-bold" htmlFor="online_sevas">Enable Online Sevas</label>
                                </div>
                            </div>
                        </div>

                        <hr />

                        {/* ── Invoice Numbering Section ── */}
                        <h5 className="mb-3 mt-4 text-secondary">
                            <i className="fa fa-file-invoice me-2"></i>Invoice Numbering Configuration
                        </h5>
                        <div className="alert alert-info small">
                            <strong>Unified Invoice Numbers:</strong> One sequential invoice number is shared across <strong>all modules</strong> — Room Booking (CRO &amp; Online), Guru Seva, Online Sevas, and Donations. Configure the prefix and reset period below.
                        </div>

                        <div className="row g-3 mb-3">
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Invoice Prefix</label>
                                <input
                                    type="text"
                                    name="invoice_prefix"
                                    className="form-control"
                                    placeholder="e.g. DVHR/26/ or INV/"
                                    value={formData.invoice_prefix}
                                    onChange={handleChange}
                                    maxLength={20}
                                />
                                <div className="form-text">Text that appears before the number in every invoice.</div>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Reset Period</label>
                                <select
                                    name="invoice_reset_period"
                                    className="form-select"
                                    value={formData.invoice_reset_period}
                                    onChange={handleChange}
                                    style={{
                                        height: '45px',
                                        padding: '5px 12px',
                                        fontSize: '15px',
                                        lineHeight: '1.5',
                                        display: 'block',
                                        width: '100%',
                                        backgroundColor: '#fff',
                                        color: '#333'
                                    }}
                                >
                                    <option value="none">Never Reset (continuous)</option>
                                    <option value="yearly">Yearly (Indian FY: Apr–Mar)</option>
                                    <option value="half-yearly">Half-Yearly (Apr–Sep / Oct–Mar)</option>
                                    <option value="monthly">Monthly</option>
                                </select>
                                <div className="form-text">How often the counter resets back to 1.</div>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Number Padding (digits)</label>
                                <input
                                    type="number"
                                    name="invoice_padding"
                                    className="form-control"
                                    min="1"
                                    max="10"
                                    value={formData.invoice_padding}
                                    onChange={handleChange}
                                />
                                <div className="form-text">Total digits for the number part (e.g. 4 → 0001).</div>
                            </div>
                        </div>

                        {/* Live Preview */}
                        <div className="mb-3">
                            <label className="form-label fw-bold text-muted small">Preview (first invoice would look like):</label>
                            <div className="d-inline-block bg-light border rounded px-3 py-2 ms-2">
                                <code className="fs-6 text-primary fw-bold">{buildPreviewInvoice()}</code>
                            </div>
                        </div>

                        {/* Reset Invoice Counter */}
                        <div className="border rounded p-3 bg-light mb-4">
                            <p className="fw-bold mb-1 text-danger">⚠️ Reset Invoice Counter</p>
                            <p className="small text-muted mb-2">
                                Deletes all period counters and restarts numbering from <strong>0001</strong>. Use this only when changing fiscal year manually or fixing numbering errors. This action is <strong>permanent</strong>.
                            </p>
                            <button
                                type="button"
                                id="btn-reset-invoice-counter"
                                className="btn btn-outline-danger btn-sm"
                                onClick={handleResetInvoiceCounter}
                                disabled={invoiceReset.loading}
                            >
                                {invoiceReset.loading ? '🔄 Resetting...' : '🗑️ Reset Invoice Counter'}
                            </button>
                            {invoiceReset.msg && (
                                <div className={`alert mt-2 mb-0 ${invoiceReset.msg.startsWith('✅') ? 'alert-success' : 'alert-danger'} small`}>
                                    {invoiceReset.msg}
                                </div>
                            )}
                        </div>

                        <hr />

                        {/* ── Visitor Counter Section ── */}
                        <h5 className="mb-3 mt-4 text-secondary">Visitor Counter</h5>
                        <div className="alert alert-warning small">
                            <strong>Note:</strong> Resetting the counter will set the public visitor count displayed on the Home page back to <strong>0</strong>. This action is permanent and cannot be undone.
                        </div>
                        <div className="d-flex align-items-center gap-3 mb-2">
                            <button
                                type="button"
                                id="btn-reset-visitor-counter"
                                className="btn btn-danger"
                                onClick={handleResetVisitorCount}
                                disabled={visitorReset.loading}
                            >
                                {visitorReset.loading ? 'Resetting...' : '🔄 Reset Visitor Counter to 0'}
                            </button>
                        </div>
                        {visitorReset.msg && (
                            <div className={`alert mt-2 ${visitorReset.msg.startsWith('✅') ? 'alert-success' : 'alert-danger'
                                } small`}>
                                {visitorReset.msg}
                            </div>
                        )}

                        <hr />

                        <div className="d-flex justify-content-end">
                            <button type="submit" className="btn btn-primary px-5" disabled={isUpdating}>
                                {isUpdating ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ApplicationSettings;
